// ─── Teams tab helpers (V4-p) ─────────────────────────────────────────────────
//
// Pure functions, kept out of TeamsTab.jsx so that file only exports
// components (react-refresh) and so they are unit-testable on their own: the
// period arithmetic, the Persian words for the server's refusals, and what a
// reshuffle answer means to an admin.

import { faDigits } from '../../lib/numerals'
import { JALALI_MONTHS_FA } from './constants'

/** A shamsi month key as the backend takes it: `YYYY-MM`, e.g. `1405-07`. */
export const PERIOD_KEY_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/

export function isPeriodKey(value) {
  return typeof value === 'string' && PERIOD_KEY_PATTERN.test(value)
}

/** `1405-07` shifted by `delta` months: `1405-12` + 1 → `1406-01`. A malformed key comes back as it is. */
export function shiftPeriod(key, delta) {
  if (!isPeriodKey(key)) return key
  const [year, month] = key.split('-').map(Number)
  const index = year * 12 + (month - 1) + delta
  const nextYear = Math.floor(index / 12)
  const nextMonth = (index % 12) + 1
  return `${nextYear}-${String(nextMonth).padStart(2, '0')}`
}

/** `1405-07` → `مهر ۱۴۰۵`; anything else as it came. */
export function periodLabel(key) {
  if (!isPeriodKey(key)) return key ?? '—'
  const [year, month] = key.split('-').map(Number)
  return `${JALALI_MONTHS_FA[month - 1]} ${faDigits(year)}`
}

/**
 * `teams.minutesBuckets` (e.g. `[[0,10],[11,20],[21,999]]`) and a team's
 * bucket index → `۱۱–۲۰ دقیقه`. The top band's 999 reads as «به بالا». Without
 * the setting (not loaded, or the index is past the list because the
 * setting changed since the team was built) it falls back to «سطل n».
 */
export function describeBucket(buckets, index) {
  const band = Array.isArray(buckets) ? buckets[index] : null
  if (!Array.isArray(band) || band.length !== 2) return `سطل ${faDigits(Number(index) + 1)}`
  const [from, to] = band
  if (to >= 999) return `${faDigits(from)}+ دقیقه`
  return `${faDigits(from)}–${faDigits(to)} دقیقه`
}

/** Whether a team has no seat left at the current `maxSize`. */
export function isFull(team, maxSize) {
  return Number.isFinite(maxSize) && team.memberCount >= maxSize
}

/** How a learner came to be in the team. */
export const JOIN_REASON_FA = { monthly: 'تیم‌بندی ماهانه', join: 'پس از آنبوردینگ', admin_move: 'جابه‌جایی ادمین' }

/**
 * The refusals of `admin-teams_move` (and the 404 of `_detail`) in the
 * admin's own language, or null when this is not one of them — the caller
 * then falls back to `errorMessage`. `team_full` is the one a busy period
 * produces: the list the dialog offered was read a moment ago and another
 * admin or a new learner took the last seat since.
 */
export function teamRefusal(error) {
  const status = error?.response?.status
  const code = error?.response?.data?.error
  if (code === 'team_full' || status === 409) return 'این تیم پر است و جای خالی ندارد. تیم دیگری انتخاب کنید یا فهرست را تازه کنید.'
  if (code === 'team_not_found') return 'این تیم دیگر وجود ندارد. فهرست را تازه کنید.'
  if (code === 'user_not_found') return 'این کاربر پیدا نشد.'
  if (code === 'admin_jwt_required' || status === 403) {
    return 'جابه‌جایی فقط با ورود از طریق شماره تلفن ممکن است؛ کلید مشترک ادمین نمی‌تواند در گزارش ممیزی ثبت شود.'
  }
  return null
}

/**
 * What a `admin-teams_reshuffle` answer means, as `{ tone, text }`. A real run
 * is only ever sent after a dry run of the same period was looked at, so
 * `already-done` here is information (the month was built before), not an
 * error.
 */
export function describeOutcome(result) {
  switch (result?.outcome) {
    case 'dry-run': return { tone: 'info', text: 'اجرای خشک: هیچ چیزی نوشته نشد. نتیجه را بررسی کنید.' }
    case 'ran': return { tone: 'ok', text: 'تیم‌بندی این دوره انجام شد.' }
    case 'already-done': return { tone: 'info', text: 'تیم‌بندی این دوره قبلاً با موفقیت انجام شده است؛ دوباره اجرا نشد.' }
    case 'already-running': return { tone: 'warn', text: 'یک تیم‌بندی برای همین دوره همین حالا در حال اجراست. کمی بعد فهرست را تازه کنید.' }
    default: return { tone: 'warn', text: `پاسخ ناشناخته: ${result?.outcome ?? '—'}` }
  }
}

/** The summary of a reshuffle answer — a dry run carries it directly, a real run inside `run`. */
export function summaryOf(result) {
  return result?.summary ?? result?.run?.summary ?? null
}
